import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RiDownloadLine } from "react-icons/ri";
import ExcelJS from "exceljs";
import { useSelector, useDispatch } from "react-redux";

import css from "./DeliveriesComponent.module.css";
import { SliderCalendar } from "../../../ui";
import DeliveriesListComponent from "./DeliveriesListComponent";
import MapComponent from "./MapComponent";
import { ordersActions } from "../../../store/slices/orders-slices";
import { useInitCalendarDate } from "../../../helpers";
import { ordersService } from "../../../services";
import { toast } from "react-toastify";
import { Button, Space, DatePicker } from "antd";
import { getDefaultDateRange } from "../../../helpers/getDefaultDateRange";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const DeliveriesComponent = () => {
	useInitCalendarDate(); // встановлюємо дату перед рендером

	const { t, i18n } = useTranslation();
	const [tableWidth, setTableWidth] = useState(50);
	const [hoveredDelivery, setHoveredDelivery] = useState(null);
	const [localDeliveries, setLocalDeliveries] = useState(null); // Local state for deliveries
	const resizeHandleRef = useRef(null);
	const containerRef = useRef(null);
	const isResizing = useRef(false);
	const dispatch = useDispatch();
	const [loadingDeliveriesSheet, setLoadingDeliveriesSheet] = useState(false);
	const [loadingStickersSheet, setLoadingStickersSheet] = useState(false);
	const [loadingExcludedIngredientsSheet, setLoadingExcludedIngredientsSheet] =
		useState(false);
	const [
		loadingDeliveriesSheetForKitchen,
		setLoadingDeliveriesSheetForKitchen,
	] = useState(false);
	const deliveriesData = useSelector((state) => state.orders.deliveriesByDate);
	const { trigger } = useSelector((state) => state.orders);
	const { dateRange } = useSelector((state) => state.orders);

	const formatDate = (dateString) => {
		if (!dateString) return "";
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, "0");
		const month = (date.getMonth() + 1).toString().padStart(2, "0");
		const year = date.getFullYear();

		// Convert to 12-hour format
		let hour = date.getHours();
		const ampm = hour >= 12 ? "PM" : "AM";
		hour = hour % 12;
		hour = hour ? hour : 12; // 0 should be 12
		const hourStr = hour.toString().padStart(2, "0");

		const minute = date.getMinutes().toString().padStart(2, "0");
		const second = date.getSeconds().toString().padStart(2, "0");

		return `${day}/${month}/${year}`;
	};

	useEffect(() => {
		if (dateRange.startDate && dateRange.endDate) {
			dispatch(
				ordersActions.getDeliveriesByDateRange({
					startDate: dateRange.startDate,
					endDate: dateRange.endDate,
				}),
			);
		}
	}, [dateRange.startDate, dateRange.endDate, trigger, dispatch]);

	// Sync local deliveries with Redux state
	useEffect(() => {
		if (deliveriesData) {
			setLocalDeliveries([...deliveriesData]); // Create a copy for local manipulation
		}
	}, [deliveriesData]);

	// Trigger map resize when table width changes
	useEffect(() => {
		const timer = setTimeout(() => {
			window.dispatchEvent(new Event("resize"));
		}, 100);

		return () => clearTimeout(timer);
	}, [tableWidth]);

	const handleClickXls = async () => {
		setLoadingDeliveriesSheet(true);
		let currentDeliveries;
		try {
			currentDeliveries = await dispatch(
				ordersActions.getDeliveriesByDateRange({
					startDate: dateRange.startDate,
					endDate: dateRange.endDate,
				}),
			).unwrap();
		} catch (e) {
			toast.error(t("Failed to fetch latest deliveries"));
			setLoadingDeliveriesSheet(false);
			return;
		}
		if (!currentDeliveries || currentDeliveries.length === 0) {
			alert(t("No deliveries data available for the selected date"));
			setLoadingDeliveriesSheet(false);
			return;
		}

		// Use the same grouping and sorting logic as the list component
		const groupedDeliveries = () => {
			// Helper function to calculate distance between two points using Haversine formula
			const calculateDistance = (lat1, lng1, lat2, lng2) => {
				const R = 6371; // Earth's radius in kilometers
				const dLat = ((lat2 - lat1) * Math.PI) / 180;
				const dLng = ((lng2 - lng1) * Math.PI) / 180;
				const a =
					Math.sin(dLat / 2) * Math.sin(dLat / 2) +
					Math.cos((lat1 * Math.PI) / 180) *
						Math.cos((lat2 * Math.PI) / 180) *
						Math.sin(dLng / 2) *
						Math.sin(dLng / 2);
				const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
				return R * c; // Distance in kilometers
			};

			// Group deliveries by courier
			const groups = {};

			currentDeliveries.forEach((delivery) => {
				const courierKey = delivery.assignedCourier || "unassigned";
				if (!groups[courierKey]) {
					groups[courierKey] = [];
				}
				groups[courierKey].push(delivery);
			});

			// Sort deliveries within each group
			Object.keys(groups).forEach((courierKey) => {
				groups[courierKey].sort((a, b) => {
					// First check if both have custom order (from drag and drop reordering)
					if (a.customOrder !== undefined && b.customOrder !== undefined) {
						return a.customOrder - b.customOrder;
					}

					// If only one has custom order, prioritize it based on the order value
					if (a.customOrder !== undefined && b.customOrder === undefined) {
						return -1; // a comes first
					}
					if (a.customOrder === undefined && b.customOrder !== undefined) {
						return 1; // b comes first
					}

					// Default sorting: by delivery interval first, then by distance
					const aInterval = a.deliveryInterval || "";
					const bInterval = b.deliveryInterval || "";
					const intervalCompare = aInterval.localeCompare(bInterval);
					if (intervalCompare !== 0) return intervalCompare;

					// Then sort by distance from depot for same interval
					if (a.latitude && a.longitude && b.latitude && b.longitude) {
						const depotLat = 41.72252;
						const depotLng = 44.780379;

						const aDistanceFromDepot = calculateDistance(
							depotLat,
							depotLng,
							a.latitude,
							a.longitude,
						);
						const bDistanceFromDepot = calculateDistance(
							depotLat,
							depotLng,
							b.latitude,
							b.longitude,
						);

						return aDistanceFromDepot - bDistanceFromDepot;
					}

					return 0;
				});
			});

			// Sort groups by courier name (assigned couriers first, then alphabetically)
			const sortedGroups = {};
			const sortedKeys = Object.keys(groups).sort((a, b) => {
				if (a === "unassigned" && b !== "unassigned") return 1;
				if (a !== "unassigned" && b === "unassigned") return -1;
				return a.localeCompare(b);
			});

			sortedKeys.forEach((key) => {
				sortedGroups[key] = groups[key];
			});

			return sortedGroups;
		};

		const grouped = groupedDeliveries();

		const workbook = new ExcelJS.Workbook();
		workbook.creator = "GWT ERP";
		workbook.lastModifiedBy = "GWT ERP";
		workbook.created = new Date();
		workbook.modified = new Date();

		// Helper function to create and style a title row
		const createTitleRow = (worksheet, text) => {
			const titleRow = worksheet.addRow([text]);
			titleRow.font = { bold: true, size: 14 };
			worksheet.mergeCells(`A1:I1`);
			worksheet.getCell("A1").alignment = { horizontal: "center" };
			titleRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "E6F4FF" },
				};
			});
		};

		// Helper function to create and style a header row
		const createHeaderRow = (worksheet) => {
			const headerRow = worksheet.addRow([
				"Date",
				"Order",
				"Type",
				"Address / Ingredients",
				"Phone / Exclude",
				"Name",
				"Debt",
				"Delivery interval",
				"Courier",
			]);
			headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
			headerRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "4472C4" },
				};
				cell.alignment = { horizontal: "center" };
			});
		};

		// Helper function to set up worksheet columns
		const setupColumns = (worksheet) => {
			worksheet.columns = [
				{ key: "date", width: 15 },
				{ key: "order", width: 15 },
				{ key: "type", width: 20 },
				{ key: "address", width: 40 },
				{ key: "phone", width: 20 },
				{ key: "name", width: 20 },
				{ key: "debt", width: 15 },
				{ key: "interval", width: 20 },
				{ key: "courier", width: 20 },
			];
		};

		// Helper function to add borders to all cells
		const addBordersToAllCells = (worksheet) => {
			worksheet.eachRow((row) => {
				row.eachCell((cell) => {
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						bottom: { style: "thin" },
						right: { style: "thin" },
					};
				});
			});
		};

		// Helper function to format nutrient values
		const formatNutrients = (dish) => {
			const nutrients = [
				`wgh: ${dish.weightNetto || 0}`,
				`enrg: ${dish.energy || 0}`,
				`prt: ${dish.proteins || 0}`,
				`fat: ${dish.fats || 0}`,
				`crbh: ${dish.carbohydrates || 0}`,
			];
			return nutrients.join(" / ");
		};

		// Create and populate the Deliveries worksheet
		const deliveriesWorksheet = workbook.addWorksheet("Deliveries");
		createTitleRow(
			deliveriesWorksheet,
			`Deliveries on: ${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`,
		);
		setupColumns(deliveriesWorksheet);
		createHeaderRow(deliveriesWorksheet);

		// Add data rows to Deliveries worksheet
		Object.entries(grouped).forEach(([courierKey, deliveriesInGroup]) => {
			// Add group header row
			const groupName =
				courierKey === "unassigned" ? "Unassigned Deliveries" : courierKey;
			const groupHeaderRow = deliveriesWorksheet.addRow({
				date: groupName,
				order: "",
				type: "",
				address: "",
				phone: "",
				name: "",
				debt: "",
				interval: "",
				courier: "",
			});

			// Style group header row
			groupHeaderRow.font = { bold: true, size: 12 };
			groupHeaderRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "B4C6E7" },
				};
			});

			// Add deliveries for this group
			deliveriesInGroup.forEach((delivery) => {
				const address = [
					delivery.devlieryCity,
					delivery.deliveryStreet,
					delivery.buidlingNumber,
					delivery.commentForDelivery,
				]
					.filter(Boolean)
					.join(", ");

				// For Deliveries worksheet, don't include excluded products
				const row = deliveriesWorksheet.addRow({
					date: formatDate(delivery.date),
					order: delivery.id,
					type: delivery.dietName?.en || delivery.dietName?.ru || "",
					address: address,
					phone: delivery.contact, // Don't include excluded products here
					name: delivery.clientName,
					debt: delivery.debt > 0 ? delivery.debt : "",
					interval: delivery.deliveryInterval,
					courier: delivery.assignedCourier || "",
				});

				row.eachCell((cell) => {
					cell.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "FFE5CC" },
					};
				});
			});
		});

		addBordersToAllCells(deliveriesWorksheet);

		// Create and populate the Full worksheet
		const fullWorksheet = workbook.addWorksheet("Full");
		createTitleRow(
			fullWorksheet,
			`Deliveries on: ${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`,
		);
		setupColumns(fullWorksheet);
		createHeaderRow(fullWorksheet);

		// Add data rows to Full worksheet with dishes
		Object.entries(grouped).forEach(([courierKey, deliveriesInGroup]) => {
			// Add group header row
			const groupName =
				courierKey === "unassigned" ? "Unassigned Deliveries" : courierKey;
			const groupHeaderRow = fullWorksheet.addRow({
				date: groupName,
				order: "",
				type: "",
				address: "",
				phone: "",
				name: "",
				debt: "",
				interval: "",
				courier: "",
			});

			// Style group header row
			groupHeaderRow.font = { bold: true, size: 12 };
			groupHeaderRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "B4C6E7" },
				};
			});

			// Add deliveries for this group
			deliveriesInGroup.forEach((delivery) => {
				const address = [
					delivery.devlieryCity,
					delivery.deliveryStreet,
					delivery.buidlingNumber,
					delivery.commentForDelivery,
				]
					.filter(Boolean)
					.join(", ");

				// For order-level, don't include excluded products
				// Add order row
				const orderRow = fullWorksheet.addRow({
					date: formatDate(delivery.date),
					order: delivery.id,
					type: delivery.dietName?.en || delivery.dietName?.ru || "",
					address: address,
					phone: delivery.contact,
					name: delivery.clientName,
					debt: delivery.debt > 0 ? delivery.debt : "",
					interval: delivery.deliveryInterval,
					courier: delivery.assignedCourier || "",
				});

				orderRow.eachCell((cell) => {
					cell.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "FFE5CC" },
					};
				});

				// Add dish rows if dishes exist
				if (delivery.dishes && delivery.dishes.length > 0) {
					delivery.dishes.forEach((dish) => {
						const currentLanguage = i18n.language;
						const dishName =
							dish.name?.[currentLanguage] ||
							dish.name?.en ||
							dish.name?.ru ||
							"";

						// Format nutrient values with spaces
						const specs = formatNutrients(dish);

						// Get excluded products for this specific dish
						let excludedText = "";
						if (dish.excluded && dish.excluded.length > 0) {
							const excludedProducts = [];
							dish.excluded.forEach((product) => {
								const productName =
									product?.[currentLanguage] ||
									product?.en ||
									product?.ru ||
									"";
								if (productName && !excludedProducts.includes(productName)) {
									excludedProducts.push(productName);
								}
							});

							if (excludedProducts.length > 0) {
								excludedText = excludedProducts.join(", ");
							}
						}

						const dishRow = fullWorksheet.addRow({
							date: formatDate(delivery.date),
							order: dishName,
							type: specs,
							address: (dish.ingredients || [])
								.map(
									(ing) =>
										ing.name?.[currentLanguage] ||
										ing.name?.en ||
										ing.name?.ru ||
										"",
								)
								.filter(Boolean)
								.join(", "),
							phone: excludedText,
							name: dish.img || "",
							debt: "",
							interval: "",
							courier: "",
						});

						dishRow.eachCell((cell) => {
							cell.fill = {
								type: "pattern",
								pattern: "solid",
								fgColor: { argb: "FFFFFF" },
							};
						});
					});
				}
			});
		});

		addBordersToAllCells(fullWorksheet);

		// Generate and download the Excel file
		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `deliveries_${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}.xlsx`;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
		setLoadingDeliveriesSheet(false);
	};

	const handleClickXlsForStickers = async () => {
		try {
			setLoadingStickersSheet(true);

			const response = await ordersService.getKitchenSheetData(
				dateRange.startDate,
				dateRange.endDate,
			);

			if (!response || response.length === 0) {
				toast.warning(
					t("No delivery data available for the selected date range"),
				);
				return;
			}

			const calculateNutrition = (ingredients) => {
				const totals = {
					proteins: 0,
					carbohydrates: 0,
					fats: 0,
					energy: 0,
				};

				if (ingredients && Array.isArray(ingredients)) {
					ingredients.forEach((ingredient) => {
						totals.proteins += parseFloat(ingredient.proteins || 0);
						totals.carbohydrates += parseFloat(ingredient.carbohydrates || 0);
						totals.fats += parseFloat(ingredient.fats || 0);
						totals.energy += parseFloat(ingredient.energy || 0);
					});
				}

				return {
					proteins: Math.round(totals.proteins),
					carbohydrates: Math.round(totals.carbohydrates),
					fats: Math.round(totals.fats),
					energy: Math.round(totals.energy),
				};
			};

			const addBordersToAllCells = (worksheet) => {
				worksheet.eachRow((row) => {
					row.eachCell((cell) => {
						cell.border = {
							top: { style: "thin" },
							left: { style: "thin" },
							bottom: { style: "thin" },
							right: { style: "thin" },
						};
					});
				});
			};

			const workbook = new ExcelJS.Workbook();
			workbook.creator = "GWT ERP";
			workbook.created = new Date();

			const worksheet = workbook.addWorksheet("Stickers");

			worksheet.columns = [
				{ key: "name", width: 28 },
				{ key: "type", width: 28 },
				{ key: "name_of_dish", width: 28 },
				{ key: "ingredients", width: 72 },
				{ key: "notes", width: 20 },
				{ key: "calories", width: 12 },
				{ key: "protein", width: 12 },
				{ key: "carbs", width: 12 },
				{ key: "fats", width: 12 },
				{ key: "status", width: 14 },
			];

			const titleRow = worksheet.addRow([
				`Stickers — ${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`,
				"",
				"",
				"",
				"",
				"",
				"",
				"",
				"",
				"",
			]);
			titleRow.font = { bold: true, size: 14 };
			worksheet.mergeCells("A1:J1");
			worksheet.getCell("A1").alignment = { horizontal: "center" };
			titleRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "E6F4FF" },
				};
			});

			const headerRow = worksheet.addRow([
				"Name",
				"Type",
				"Name of Product",
				"Ingredients",
				"Excluded Ingredients",
				"Calories",
				"Protein",
				"Carbs",
				"Fats",
				"Status",
			]);
			headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
			headerRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "4472C4" },
				};
				cell.alignment = { horizontal: "center", vertical: "middle" };
			});

			const currentLanguage = i18n.language;

			response.forEach((dateData) => {
				dateData.clients?.forEach((client) => {
					const clientName = client.clientName || "";

					client.orders?.forEach((order) => {
						const mealName =
							order.meal?.name?.[currentLanguage] ||
							order.meal?.name?.en ||
							order.meal?.name?.ru ||
							order.meal?.name?.ge ||
							"";

						const ingredientsText = (order.ingredients || [])
							.filter((product) => product.excluded === false)
							.map((product) => {
								const ingName =
									product.name?.[currentLanguage] ||
									product.name?.en ||
									product.name?.ru ||
									product.name?.ge ||
									"";
								// const w = product.weight;
								// if (!ingName && (w === undefined || w === null || w === "")) {
								//   return "";
								// }
								// const grams =
								//   w !== undefined && w !== null && w !== ""
								//     ? `${String(w).replace(/g$/i, "")}g`
								//     : "";
								return ingName?.trim();
							})
							.filter(Boolean)
							.join(" / ");

						const noteText = (order.ingredients || [])
							.filter((product) => product.excluded === true)
							.map((product) => {
								const ingName =
									product.name?.[currentLanguage] ||
									product.name?.en ||
									product.name?.ru ||
									product.name?.ge ||
									"";
								return ingName?.trim();
							})
							.filter(Boolean)
							.join(" / ");

						const ingredientNutrition = calculateNutrition(order.ingredients);
						const nutrition = {
							energy:
								order.dishEnergy != null && order.dishEnergy > 0
									? Math.round(order.dishEnergy)
									: ingredientNutrition.energy,
							proteins:
								order.dishProteins != null && order.dishProteins > 0
									? Math.round(order.dishProteins)
									: ingredientNutrition.proteins,
							fats:
								order.dishFats != null && order.dishFats > 0
									? Math.round(order.dishFats)
									: ingredientNutrition.fats,
							carbohydrates:
								order.dishCarbs != null && order.dishCarbs > 0
									? Math.round(order.dishCarbs)
									: ingredientNutrition.carbohydrates,
						};
						const dishName =
							order.dishName?.[currentLanguage] ||
							order.dishName?.en ||
							order.dishName?.ru ||
							order.dishName?.ge ||
							"";

						const dataRow = worksheet.addRow({
							name: clientName,
							type: mealName,
							name_of_dish: `${order.weightNetto}g / ${dishName}`,
							ingredients: `${ingredientsText}`,
							notes: noteText,
							calories: nutrition.energy,
							protein: nutrition.proteins,
							carbs: nutrition.carbohydrates,
							fats: nutrition.fats,
							status: "",
						});

						dataRow.eachCell((cell, colNumber) => {
							cell.fill = {
								type: "pattern",
								pattern: "solid",
								fgColor: { argb: "FFFFFF" },
							};
							if (colNumber >= 5 && colNumber <= 8) {
								cell.alignment = { horizontal: "center", vertical: "middle" };
							}
						});
						dataRow.getCell("ingredients").alignment = {
							wrapText: true,
							vertical: "top",
						};
					});
				});
			});

			addBordersToAllCells(worksheet);

			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `stickers_${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}.xlsx`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			toast.success(t("Stickers sheet generated successfully"));
		} catch (error) {
			console.error("Failed to get delivery shipments data:", error);
			toast.error(t("Failed to get delivery shipments data"));
		} finally {
			setLoadingStickersSheet(false);
		}
	};

	const handleClickXlsForExcludedIngredients = async () => {
		try {
			setLoadingExcludedIngredientsSheet(true);

			const response = await ordersService.getKitchenSheetData(
				dateRange.startDate,
				dateRange.endDate,
			);

			if (!response || response.length === 0) {
				toast.warning(
					t("No delivery data available for the selected date range"),
				);
				return;
			}

			const addBordersToAllCells = (worksheet) => {
				worksheet.eachRow((row) => {
					row.eachCell((cell) => {
						cell.border = {
							top: { style: "thin" },
							left: { style: "thin" },
							bottom: { style: "thin" },
							right: { style: "thin" },
						};
					});
				});
			};

			const workbook = new ExcelJS.Workbook();
			workbook.creator = "GWT ERP";
			workbook.created = new Date();

			const worksheet = workbook.addWorksheet("Excluded Ingredients");

			worksheet.columns = [
				{ key: "clientName", width: 28 },
				{ key: "dishName", width: 28 },
				{ key: "excludedIngredients", width: 72 },
			];

			const titleRow = worksheet.addRow([
				`Excluded Ingredients — ${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`,
				"",
				"",
			]);
			titleRow.font = { bold: true, size: 14 };
			worksheet.mergeCells("A1:C1");
			worksheet.getCell("A1").alignment = { horizontal: "center" };
			titleRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "E6F4FF" },
				};
			});

			const headerRow = worksheet.addRow([
				"Client Name",
				"Dish Name",
				"Excluded Ingredients",
			]);
			headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
			headerRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "4472C4" },
				};
				cell.alignment = { horizontal: "center", vertical: "middle" };
			});

			const currentLanguage = i18n.language;

			let excludedIngredientsExists = false;

			response.forEach((dateData) => {
				dateData.clients?.forEach((client) => {
					const clientName = client.clientName || "";

					client.orders?.forEach((order) => {
						const excludedNames = (order.ingredients || [])
							.filter((product) => product.excluded === true)
							.map((product) => {
								const ingName =
									product.name?.[currentLanguage] ||
									product.name?.en ||
									product.name?.ru ||
									product.name?.ge ||
									"";
								return ingName.trim();
							})
							.filter(Boolean);

						const excludedIngredientsText = [...new Set(excludedNames)].join(
							"\n",
						);

						const dishName =
							order.dishName?.[currentLanguage] ||
							order.dishName?.en ||
							order.dishName?.ru ||
							order.dishName?.ge ||
							"";

						if (excludedIngredientsText.length > 0) {
							excludedIngredientsExists = true;

							const dataRow = worksheet.addRow({
								clientName: clientName,
								dishName: dishName,
								excludedIngredients: excludedIngredientsText,
							});

							dataRow.eachCell((cell) => {
								cell.fill = {
									type: "pattern",
									pattern: "solid",
									fgColor: { argb: "FFFFFF" },
								};
								cell.alignment = { horizontal: "center", vertical: "middle" };
							});
							dataRow.getCell("excludedIngredients").alignment = {
								wrapText: true,
								vertical: "top",
							};
						}
					});
				});
			});

			if (!excludedIngredientsExists) {
				toast.warning(
					t("No excluded ingredients found for the selected date range"),
				);
				return;
			}

			addBordersToAllCells(worksheet);

			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `excluded_ingredients_${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}.xlsx`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			toast.success(t("Excluded ingredients sheet generated successfully"));
		} catch (error) {
			console.error("Failed to generate excluded ingredients sheet:", error);
			toast.error(t("Failed to generate excluded ingredients sheet"));
		} finally {
			setLoadingExcludedIngredientsSheet(false);
		}
	};

	const handleClickXlsForDeliveries = async () => {
		try {
			setLoadingDeliveriesSheetForKitchen(true);
			const response = await ordersService.getKitchenSheetData(
				dateRange.startDate,
				dateRange.endDate,
			);

			if (!response || response.length === 0) {
				toast.warning(
					t("No delivery data available for the selected date range"),
				);
				return;
			}

			const workbook = new ExcelJS.Workbook();
			workbook.creator = "GWT ERP";
			workbook.created = new Date();

			// Helper function to add borders to all cells
			const addBordersToAllCells = (worksheet) => {
				worksheet.eachRow((row) => {
					row.eachCell((cell) => {
						cell.border = {
							top: { style: "thin" },
							left: { style: "thin" },
							bottom: { style: "thin" },
							right: { style: "thin" },
						};
					});
				});
			};

			// Create single worksheet with both sections
			const worksheet = workbook.addWorksheet("Deliveries");

			// Set up columns for combined sheet
			worksheet.columns = [
				{ key: "refNumber", width: 18 },
				{ key: "name", width: 25 },
				{ key: "address", width: 80 },
				{ key: "city", width: 15 },
				{ key: "phone", width: 20 },
				{ key: "phone2", width: 20 },
				{ key: "shipmentDesc", width: 25 },
				{ key: "boxes", width: 12 },
				{ key: "deliveryDate", width: 20 },
				{ key: "deliveryTime", width: 25 },
				{ key: "internalRef", width: 30 },
			];

			// Add title row with two sections
			const titleRow = worksheet.addRow([
				"\\",
				"Consignee",
				"",
				"",
				"",
				"",
				"Shipments - OPS Data",
				"",
				"",
				"",
				"",
			]);
			titleRow.font = { bold: true, size: 14, color: { argb: "000000" } };
			worksheet.mergeCells("B1:F2");
			worksheet.mergeCells("G1:K2");
			worksheet.getCell("B1").alignment = {
				horizontal: "center",
				vertical: "middle",
			};
			worksheet.getCell("G1").alignment = {
				horizontal: "center",
				vertical: "middle",
			};
			titleRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "B4C7E7" },
				};
			});

			// Add header row with all columns
			const headerRow = worksheet.addRow([
				"Reference Number",
				"Consignee Name",
				"Consignee Address",
				"Consignee City",
				"Consignee Phone Number",
				"Consignee Phone Number 2",
				"Shipment Description",
				"Boxes #",
				"Preferred Delivery Date",
				"Preferred Delivery Time",
				"Internal Reference",
			]);
			headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
			headerRow.eachCell((cell) => {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "4472C4" },
				};
				cell.alignment = { horizontal: "center", vertical: "middle" };
			});

			// Add data rows
			let referenceNumber = 1;
			response.forEach((dateData) => {
				const deliveryDate = formatDate(dateData.date);

				dateData.clients.forEach((client) => {
					// Add combined row with all data
					const dataRow = worksheet.addRow({
						refNumber: referenceNumber,
						name: client.clientName || "",
						address:
							`${client.clientAddress} \nDelivery comment: ${client.clientDeliveryComments}` ||
							"",
						city: client.clientCity || "",
						phone: client.clientPhone || "",
						phone2: "", // Empty as per the image
						shipmentDesc: "Meal Plan",
						boxes: client.totalOrders || 1,
						deliveryDate: deliveryDate,
						deliveryTime: client.clientDeliveryInterval || "",
						internalRef: client.clientName
							? client.clientName.split("(")[0].trim()
							: "",
					});

					dataRow.eachCell((cell) => {
						cell.fill = {
							type: "pattern",
							pattern: "solid",
							fgColor: { argb: "FFFFFF" },
						};
					});

					referenceNumber++;
				});
			});

			// Add borders to all cells
			addBordersToAllCells(worksheet);

			// Generate and download the Excel file
			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `deliveries_shipments_${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}.xlsx`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			toast.success(t("Delivery shipments sheet generated successfully"));
		} catch (error) {
			console.error("Failed to get delivery shipments data:", error);
			toast.error(t("Failed to get delivery shipments data"));
		} finally {
			setLoadingDeliveriesSheetForKitchen(false);
		}
	};

	const startResizing = (e) => {
		isResizing.current = true;
		resizeHandleRef.current.classList.add("resizing");
		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", stopResizing);
		e.preventDefault();
	};

	const stopResizing = () => {
		isResizing.current = false;
		if (resizeHandleRef.current) {
			resizeHandleRef.current.classList.remove("resizing");
		}
		document.removeEventListener("mousemove", handleMouseMove);
		document.removeEventListener("mouseup", stopResizing);
	};

	const handleMouseMove = (e) => {
		if (!isResizing.current || !containerRef.current) return;
		const containerRect = containerRef.current.getBoundingClientRect();
		const newWidth =
			((e.clientX - containerRect.left) / containerRect.width) * 100;
		if (newWidth >= 25 && newWidth <= 75) {
			setTableWidth(newWidth);
			// Trigger map resize after width change
			setTimeout(() => {
				window.dispatchEvent(new Event("resize"));
			}, 10);
		}
	};

	const handleDeliveryUpdate = (
		updatedDelivery,
		action = "update",
		metadata = null,
	) => {
		if (!localDeliveries) return;

		if (action === "bulk-move" && metadata) {
			// Handle bulk courier assignment change
			console.log("Bulk moving deliveries:", {
				deliveryIds: metadata.deliveryIds,
				fromCourier: metadata.fromCourier,
				toCourier: metadata.toCourier,
				count: metadata.deliveryIds.length,
			});

			const updatedLocalDeliveries = localDeliveries.map((delivery) =>
				metadata.deliveryIds.includes(delivery.id)
					? { ...delivery, assignedCourier: metadata.newCourier }
					: delivery,
			);
			setLocalDeliveries(updatedLocalDeliveries);
		} else if (action === "reorder" && metadata) {
			// Handle reordering within the same courier group
			console.log("Reordering delivery within group:", {
				deliveryId: updatedDelivery.id,
				group: metadata.fromGroup,
				fromIndex: metadata.fromIndex,
				toIndex: metadata.toIndex,
			});

			// Find the delivery being moved
			const deliveryToMove = localDeliveries.find(
				(d) => d.id === updatedDelivery.id,
			);
			if (!deliveryToMove) return;

			// Get all deliveries for this courier group, sorted by current order
			const courierKey = metadata.fromGroup;
			const groupDeliveries = localDeliveries
				.filter((d) => (d.assignedCourier || "unassigned") === courierKey)
				.sort((a, b) => {
					// First check if both have custom order (from drag and drop reordering)
					if (a.customOrder !== undefined && b.customOrder !== undefined) {
						return a.customOrder - b.customOrder;
					}

					// If only one has custom order, prioritize it based on the order value
					if (a.customOrder !== undefined && b.customOrder === undefined) {
						return -1; // a comes first
					}
					if (a.customOrder === undefined && b.customOrder !== undefined) {
						return 1; // b comes first
					}

					// Default sorting: by delivery interval first, then by distance
					const aInterval = a.deliveryInterval || "";
					const bInterval = b.deliveryInterval || "";
					const intervalCompare = aInterval.localeCompare(bInterval);
					if (intervalCompare !== 0) return intervalCompare;

					// Then sort by distance from depot for same interval
					if (a.latitude && a.longitude && b.latitude && b.longitude) {
						const depotLat = 41.72252;
						const depotLng = 44.780379;

						const calculateDistance = (lat1, lng1, lat2, lng2) => {
							const R = 6371; // Earth's radius in kilometers
							const dLat = ((lat2 - lat1) * Math.PI) / 180;
							const dLng = ((lng2 - lng1) * Math.PI) / 180;
							const a =
								Math.sin(dLat / 2) * Math.sin(dLat / 2) +
								Math.cos((lat1 * Math.PI) / 180) *
									Math.cos((lat2 * Math.PI) / 180) *
									Math.sin(dLng / 2) *
									Math.sin(dLng / 2);
							const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
							return R * c; // Distance in kilometers
						};

						const aDistanceFromDepot = calculateDistance(
							depotLat,
							depotLng,
							a.latitude,
							a.longitude,
						);
						const bDistanceFromDepot = calculateDistance(
							depotLat,
							depotLng,
							b.latitude,
							b.longitude,
						);

						return aDistanceFromDepot - bDistanceFromDepot;
					}

					return 0;
				});

			// Remove the delivery from its current position and insert at new position
			const currentIndex = groupDeliveries.findIndex(
				(d) => d.id === deliveryToMove.id,
			);
			const reorderedGroup = [...groupDeliveries];
			reorderedGroup.splice(currentIndex, 1); // Remove from current position
			reorderedGroup.splice(metadata.toIndex, 0, deliveryToMove); // Insert at new position

			// Add a custom order property to maintain the new order
			const reorderedWithOrder = reorderedGroup.map((delivery, index) => ({
				...delivery,
				customOrder: index,
			}));

			// Update the full deliveries array
			const updatedDeliveries = localDeliveries.map((delivery) => {
				const reorderedDelivery = reorderedWithOrder.find(
					(rd) => rd.id === delivery.id,
				);
				return reorderedDelivery || delivery;
			});

			setLocalDeliveries(updatedDeliveries);
		} else {
			// Handle courier assignment change
			console.log("Updating delivery courier assignment:", {
				deliveryId: updatedDelivery.id,
				oldCourier: localDeliveries.find((d) => d.id === updatedDelivery.id)
					?.assignedCourier,
				newCourier: updatedDelivery.assignedCourier,
			});

			const updatedLocalDeliveries = localDeliveries.map((delivery) =>
				delivery.id === updatedDelivery.id
					? { ...delivery, assignedCourier: updatedDelivery.assignedCourier }
					: delivery,
			);
			setLocalDeliveries(updatedLocalDeliveries);
		}

		// Here you would typically make an API call to update the backend
		// For example:
		// if (action === 'reorder') {
		//     ordersService.updateDeliveryOrder(updatedDelivery.id, metadata)
		// } else {
		//     ordersService.updateDeliveryCourier(updatedDelivery.id, updatedDelivery.assignedCourier)
		// }
		//   .then(() => {
		//     // Optionally refresh data after successful update to sync with backend
		//     dispatch(ordersActions.getDeliveriesByDate({ date: selectedDate }));
		//   })
		//   .catch(error => {
		//     console.error('Failed to update delivery:', error);
		//     // Revert local state on error
		//     setLocalDeliveries([...deliveriesData]);
		//   });
	};

	const handleDateRangeChange = (dates) => {
		if (dates && dates.length === 2) {
			dispatch(
				ordersActions.setDateRange({
					startDate: dates[0].format("YYYY-MM-DD"),
					endDate: dates[1].format("YYYY-MM-DD"),
				}),
			);
		}
	};

	// Initialize date range and fetch dishes data
	useEffect(() => {
		if (!dateRange.startDate || !dateRange.endDate) {
			const initialRange = getDefaultDateRange(true, true);
			dispatch(ordersActions.setDateRange(initialRange));
		}
	}, [dispatch, dateRange.startDate, dateRange.endDate]);

	return (
		<section className={css.wrap}>
			{/* <div className={css.blockCalendar}>
                <SliderCalendar />
            </div> */}
			<div className={css.blockBody}>
				<div className={css.blockInfoHeader}>
					<Space
						direction="horizontal"
						size="large"
						style={{
							marginBottom: 16,
							padding: "12px 16px",
							background: "#fff",
							borderRadius: 8,
							border: "1px solid #e6e8ee",
							flexWrap: "wrap",
							width: "100%",
						}}
					>
						<RangePicker
							value={
								dateRange.startDate && dateRange.endDate
									? [dayjs(dateRange.startDate), dayjs(dateRange.endDate)]
									: null
							}
							onChange={handleDateRangeChange}
							format="YYYY-MM-DD"
							style={{ width: 300 }}
						/>

						<Button
							type="primary"
							icon={<RiDownloadLine />}
							onClick={handleClickXls}
							loading={loadingDeliveriesSheet}
						>
							Export XLS For Deliveries
						</Button>
						<Button
							type="primary"
							icon={<RiDownloadLine />}
							onClick={handleClickXlsForDeliveries}
							loading={loadingDeliveriesSheetForKitchen}
						>
							Export XLS For Kitchen
						</Button>

						{/* button for export xlsx file for sticker  */}
						<Button
							type="primary"
							icon={<RiDownloadLine />}
							onClick={handleClickXlsForStickers}
							loading={loadingStickersSheet}
						>
							Export XLS For Stickers
						</Button>

						{/* button for export xlsx file for excluded ingredients */}
						<Button
							type="primary"
							icon={<RiDownloadLine />}
							onClick={handleClickXlsForExcludedIngredients}
							loading={loadingExcludedIngredientsSheet}
						>
							Export XLS For Excluded Ingredients
						</Button>
					</Space>
				</div>

				<div className={css.content} ref={containerRef}>
					<div style={{ width: `${tableWidth}%` }}>
						<DeliveriesListComponent
							deliveries={localDeliveries || deliveriesData}
							hoveredDelivery={hoveredDelivery}
							onDeliveryHover={setHoveredDelivery}
							onDeliveryUpdate={handleDeliveryUpdate}
						/>
					</div>
					<div
						className={css.resizeHandle}
						ref={resizeHandleRef}
						onMouseDown={startResizing}
					/>
					<div style={{ width: `${100 - tableWidth}%`, height: "100%" }}>
						<MapComponent
							deliveries={localDeliveries || deliveriesData}
							hoveredDelivery={hoveredDelivery}
						/>
					</div>
				</div>
			</div>
		</section>
	);
};

export { DeliveriesComponent };
